import { api } from "../api/client";
import { ListDetail, ListSummary } from "../types/List";
import { Visibility } from "../types/Tracking";
import { Topic } from "./changes";
import { useApi } from "./useApi";
import { usePagedApi } from "./usePagedApi";

// Your lists, each saying whether it holds the work.
export const listsHolding = (workId: number) =>
  api.get<ListSummary[]>(`/lists?work_id=${workId}`);

// Lists' writes. They never wait offline: only the shelf's do (plan 019
// §3.4), so offline each fails at once and its page says so.

// The new list, as the caller reads it (a summary or the detail).
export const createList = <T = ListDetail>(name: string) =>
  api.post<T>("/lists", { name });

export const updateList = (
  id: number,
  changes: Partial<{
    name: string;
    description: string;
    visibility: Visibility;
  }>,
) => api.patch<ListDetail>(`/lists/${id}`, changes);

export const deleteList = (id: number) => api.del(`/lists/${id}`);

// A season is its own item (ADR 0030).
export const addListItem = (
  id: number,
  item: { workId: number; note?: string | null; season?: number | null },
) => api.post<ListDetail>(`/lists/${id}/items`, item);

export const removeListItem = (id: number, workId: number, season?: number) =>
  api.del(`/lists/${id}/items/${workId}${season ? `?season=${season}` : ""}`);

export const reorderList = (id: number, itemIds: number[]) =>
  api.post(`/lists/${id}/reorder`, { itemIds });

// Leave a title out of your progress through a list, or put it back.
export const skipListItem = (id: number, workId: number, skip: boolean) => {
  const path = `/lists/${id}/skips/${workId}`;
  return skip ? api.put(path, undefined) : api.del(path);
};

export const followList = (id: number, follow: boolean) => {
  const path = `/lists/${id}/follow`;
  return follow
    ? api.post<{ isFollowing: boolean }>(path, {})
    : api.del<{ isFollowing: boolean }>(path);
};

// Lists' reads, for a page to show (ADR 0055): yours, and those a work or
// someone is on, reload when your lists change; a list's rows show your
// status on each, so it reloads when your shelf does too.
const LISTS: Topic[] = ["lists"];

export const useMyLists = () =>
  useApi<ListSummary[]>("/lists", { about: LISTS });
export const useFollowedLists = () =>
  useApi<ListSummary[]>("/lists/followed", { about: LISTS });
export const useList = (id: number | string) =>
  useApi<ListDetail>(`/lists/${id}`, { about: ["lists", "shelf"] });
export const useUserLists = (username: string) =>
  useApi<ListSummary[]>(`/users/${encodeURIComponent(username)}/lists`, {
    about: LISTS,
  });
// The public lists a work is on, most popular first.
export const useWorkLists = (workId: number | string, limit: number) =>
  useApi<ListSummary[]>(`/works/${workId}/lists?limit=${limit}`, {
    about: LISTS,
  });

// Public lists by name: a few as you type (`q` empty reads nothing), or
// Browse's, a page at a time (null reads nothing; empty, every one).
export const useListSearch = (q: string, limit: number) =>
  useApi<ListSummary[]>(
    q
      ? `/lists/public?${new URLSearchParams({ q, limit: String(limit) })}`
      : null,
    { debounce: 200 },
  );
export const usePublicLists = (q: string | null, pageSize: number) =>
  usePagedApi<ListSummary>(
    q === null
      ? null
      : q
        ? `/lists/public?${new URLSearchParams({ q })}`
        : "/lists/public",
    { pageSize, keyOf: (list) => list.id, debounce: 300 },
  );
